import React from 'react';
import PropTypes from 'prop-types';
import { withFieldProps } from 'hooks/useFieldProps';
import { withStyles } from 'theme/withStyles';
import classnames from 'classnames';
import RawHtml from 'common/RawHtml';
import radioStyles from 'styles/radio';
import ErrorMsg from 'common/ErrorMsg';
import Help from 'common/Help';
import Tooltip from 'common/Tooltip';

import rootStyles from './styles/root';

const styles = (theme) => {
  const { palette, screenSizes, typography } = theme;
  return {
    input: {
      ...radioStyles(theme),
      marginRight: 18,
    },
    root: {
      ...rootStyles(theme),
      '& .radio': {
        borderBottom: `1px solid ${palette.form.border}`,
        padding: [18, 0],
      },
      '& label': {
        ...typography.p,
        ...typography.p2,
        [`@media(max-width: ${screenSizes.mobileMax}px)`]: {
          ...typography.p3,
        },
        '& > strong': {
          float: 'right',
        },
      },
      '& legend': {
        ...typography.p,
        ...typography.p2,
        color: ({ error }) => (error ? palette.error : typography.p2.color),
      },
    },
  };
};

const FundingAccountSelector = ({
  classes,
  error,
  fieldDomId,
  help,
  label,
  name,
  onChange,
  options,
  readOnly,
  tooltip,
  validate,
  value,
}) => (
  <fieldset className={classes.root}>
    <RawHtml type="legend">{label}</RawHtml>
    <Tooltip tooltip={tooltip} labeledBy={`label-${fieldDomId}`} />
    <Help help={help} id={`help-${name}`} />
    <ErrorMsg name={name} />
    {options.map((option) => (
      <div className={classnames('radio form-group')} key={`${fieldDomId}-${option.value}`}>
        <input
          type="radio"
          aria-describedby={
            classnames({
              [`help-${name}`]: help,
              [`error-${name}`]: error,
            }) || undefined
          }
          name={name}
          checked={value === option.value}
          id={`${fieldDomId}-${option.value}`}
          disabled={readOnly}
          onChange={() => {
            onChange(name, option.value);
            validate();
          }}
          className={classes.input}
          value={option.value}
        />{' '}
        <label htmlFor={`${fieldDomId}-${option.value}`}>
          <RawHtml type="span">{option.label}</RawHtml>
          <strong>{option.labelDescr}</strong>
        </label>
      </div>
    ))}
  </fieldset>
);

FundingAccountSelector.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  error: PropTypes.bool.isRequired,
  fieldDomId: PropTypes.string.isRequired,
  help: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      labelDescr: PropTypes.string,
      value: PropTypes.string,
    }),
  ).isRequired,
  readOnly: PropTypes.bool.isRequired,
  tooltip: PropTypes.string.isRequired,
  validate: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]).isRequired,
};

const FundingAccountSelectorStyled = withStyles(styles)(FundingAccountSelector);

const mapStateToProps = ({ fields }, { name }) => ({
  options: fields[name].selectorValues,
});

export default withFieldProps(mapStateToProps)(FundingAccountSelectorStyled);
